import { useState, useEffect, memo, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import {
    List,
    TextField,
    Pagination,
    BooleanField,
    SingleFieldList,
    ReferenceArrayField,
    ChipField,
    useLocaleState,
    usePermissions,
    useResourceContext,
    FunctionField,
    useNotify,
    useRefresh,
    useUpdateMany,
    useUnselectAll,
    useTranslate,
} from "react-admin";
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    pointerWithin,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Box, Grid } from "@mui/material";
import { ToolbarWithHistory } from "shared/src/components/ToolbarWithHistory";
import { queryToAttribute, queryToFilter } from "shared/src/utils/decodeQuery";
import { CategoryTree } from "./components/CategoryTree";
import { CategoryListFilter } from "./components/CategoryListFilter";
import { AccessRightsError } from "shared/src/components/AccessRightsError";
import { CategoryToolbar } from "./components/CategoryToolbar";
import { Breadcrumbs } from "shared/src/components/Breadcrumbs";
import { BulkActions } from "./components/BulkActions";
import { debounce } from "lodash";
import { ConfirmationDialog } from "shared/src/components/dialogs/ConfirmationDialog";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { CategoryDatagrid } from "./components/CategoryDatagrid";

const CategoryListComponent = () => {
    const [dndData, setDndData] = useState(null);
    const [openDndDialog, setOpenDndDialog] = useState(false);
    const [listItems, setListItems] = useState([]);
    const [checkedItems, setCheckedItems] = useState([]);
    const [filter, setFilter] = useState(queryToFilter(window.location.hash));
    const notify = useNotify();
    const refresh = useRefresh();
    const navigate = useNavigate();
    const location = useLocation();
    const translate = useTranslate();
    const [locale] = useLocaleState();
    const attribute = queryToAttribute();
    const [updateMany] = useUpdateMany();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll("products");
    const { isLoading, permissions } = usePermissions();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (over && over.id && over.data?.current?.type === "category") {
            const isMultiDrag = active.data.current?.isMultiDrag;
            const selectedItems =
                isMultiDrag && active.data.current?.selectedIds?.length > 1
                    ? active.data.current.selectedIds
                    : [active.id];
            if (selectedItems.length > 0) {
                const targetCategoryId = over.id === "-1" ? 1 : over.id;
                const targetCategoryName = over.data?.current?.name || "";

                setDndData({
                    categoryId: targetCategoryId,
                    categoryName: targetCategoryName,
                    selectedItems: selectedItems,
                });
                setOpenDndDialog(true);
            }
        }
    }, []);

    const changeCategory = useCallback(() => {
        if (!dndData) return;
        const { selectedItems, categoryId } = dndData;
        setOpenDndDialog(false);

        updateMany(
            "products",
            {
                ids: selectedItems,
                data: {
                    category: { id: categoryId },
                },
            },
            {
                onSuccess: () => {
                    setDndData(null);
                    refresh();
                    unselectAll();
                },
                onError: (e) => {
                    handleHttpError(e, notify);
                },
            }
        );
    }, [updateMany, dndData, notify, refresh, unselectAll]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedFilter = useMemo(() => filter, [JSON.stringify(filter)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleFilterChange = useCallback(
        debounce((filterInput, expandedInput) => {
            requestAnimationFrame(() => {
                Object.keys(filterInput).forEach((key) => {
                    if (!filterInput[key]) {
                        delete filterInput[key];
                    }
                });

                const filterToStr = JSON.stringify(filterInput);
                const expandedToStr = JSON.stringify(expandedInput ? expandedInput : []);

                const encodedFilter = encodeURI(`filter=${filterToStr}`);
                const encodedExpanded = encodeURI(`expanded=${expandedToStr}`);

                navigate({
                    pathname: "/categories",
                    search: `?${encodedFilter}&order=ASC&${encodedExpanded}`,
                });
                setFilter(filterInput);
            });
        }, 150),
        [navigate]
    );

    const renderIdField = useMemo(
        () => (
            <FunctionField
                label="ID"
                render={(record) => {
                    return record.labelNumber ? record.id + " / " + record.labelNumber : record.id;
                }}
            />
        ),
        []
    );

    useEffect(() => {
        setFilter(queryToFilter(window.location.hash));
    }, [location]);

    if (isLoading) {
        return <></>;
    } else if (!permissions.vote({ resource, attribute })) {
        return <AccessRightsError />;
    }

    return (
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
            <ConfirmationDialog
                open={openDndDialog}
                confirmButton={true}
                cancelButton={true}
                confirmColor="primary"
                onConfirm={changeCategory}
                onCancel={() => {
                    setDndData(null);
                    setOpenDndDialog(false);
                }}
                label={translate("equip.editCategory")}
                content={translate("equip.confirmChangeProductCategory", {
                    categoryName: dndData?.categoryName || "",
                })}
            />
            <Grid
                container
                spacing={1}
                sx={{
                    mt: 8,
                    position: "relative",
                    maxWidth: "1900px"
                }}>
                <Box
                    sx={{
                        position: "absolute",
                        left: 8,
                        top: -49,
                    }}
                >
                    <Breadcrumbs />
                </Box>
                <Grid size={{ xs: 2 }}>
                    <Box sx={{
                        marginTop: 6
                    }}>
                        <CategoryToolbar filter={memoizedFilter} />
                        <CategoryTree
                            handleFilterChange={handleFilterChange}
                            filter={memoizedFilter}
                            id={memoizedFilter?.category}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 10 }}>
                    <List
                        className="listlistlist"
                        resource="products"
                        filter={memoizedFilter}
                        perPage={50}
                        hasCreate={memoizedFilter?.category ? true : false}
                        pagination={<Pagination rowsPerPageOptions={[50, 100, 200]} />}
                        empty={false}
                        actions={
                            <ToolbarWithHistory
                                createLabel="equip.createProduct"
                                create={true}
                                export={true}
                                breadcrumbs={false}
                            />
                        }
                    >
                        <Box>
                            <CategoryListFilter
                                checkedItems={checkedItems}
                                handleFilterChange={handleFilterChange}
                                filter={memoizedFilter}
                                setCheckedItems={setCheckedItems}
                            />
                            <CategoryDatagrid
                                listItems={listItems}
                                setListItems={setListItems}
                                bulkActionButtons={<BulkActions />}
                            >
                                {renderIdField}
                                <TextField source={`name.${locale}`} label="equip.name" />
                                <TextField
                                    source={`description.${locale}`}
                                    label="equip.description"
                                />
                                <BooleanField
                                    source="isMassProduct"
                                    label="equip.isMassProduct"
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                />
                                <ReferenceArrayField
                                    key="tags"
                                    label="equip.tags"
                                    reference="tags"
                                    source="tags"
                                    sortable={false}
                                >
                                    <SingleFieldList>
                                        <ChipField source="name" />
                                    </SingleFieldList>
                                </ReferenceArrayField>
                                <TextField label="equip.manufacturer" source="manufacturer.name" />
                                <TextField label="equip.supplier" source="supplier.name" />
                                {!memoizedFilter?.category && (
                                    <TextField label="equip.category" source="category.name" />
                                )}
                                <TextField label="equip.inStock" source="quantity" />
                                <TextField label="equip.actualQuantity" source="actualQuantity" />
                            </CategoryDatagrid>
                        </Box>
                    </List>
                </Grid>
            </Grid>
        </DndContext>
    );
};

export const CategoryList = memo(CategoryListComponent, (prevProps, nextProps) => {
    return (
        JSON.stringify(prevProps.filter) === JSON.stringify(nextProps.filter) &&
        prevProps.listItems?.length === nextProps.listItems?.length &&
        prevProps.checkedItems?.length === nextProps.checkedItems?.length
    );
});
